import * as Sentry from "@sentry/react";

/**
 * Issue #484 — one component throwing blanked the whole page for a caregiver.
 * Measured on staging before this landed: zero error boundaries anywhere
 * (`ErrorBoundary|componentDidCatch` -> 0 files, control `useEffect` -> 80).
 *
 * PER-PANEL, not one at the root. The client profile mounts seven panels; a
 * single root boundary converts one panel's bug into a blank page, which is
 * the defect with a nicer message. Wrapping each panel means a caregiver loses
 * that panel and keeps the rest.
 *
 * SCOPE, narrowed after review — this does NOT close #621. An earlier version
 * of this comment claimed it closed the half of #621 that #633 could not reach.
 * That was wrong, and wrong in the direction that matters: it would have let
 * #621 be closed while the gap stood.
 *
 * A React error boundary observes render, lifecycle and constructor throws in
 * its descendants. It does not observe a rejected async loader, and it cannot
 * see a failure a panel CATCHES itself — `WatchVitalsSection` and
 * `IotEventHistorySection` both consume load failures in their own catch blocks
 * without capturing, so those stay invisible to Sentry after this change.
 *
 * What this DOES add over #633: #633 reports at the API boundary, so a panel
 * whose own render logic throws after a successful fetch produced no event.
 * That class is now captured. The caught-failure class remains #621's, and
 * #621 stays open for it.
 *
 * THE FALLBACK COPY IS A SAFETY SURFACE. A blank space where vitals belong
 * reads as "this client has no vitals" — a statement about a person, and a
 * false one. The fallback says the PANEL failed and names it, so a caregiver
 * can tell "we could not load this" from "there is nothing to show". It
 * deliberately renders no error text: a thrown message can carry a client id
 * or record, and this is the one place a stack trace would reach a caregiver's
 * screen.
 */
/** WCAG relative luminance, then the AA contrast ratio. Exported so the
 *  fallback's readability is asserted rather than eyeballed — the profile
 *  renders on `bg-black`, where a mid-grey fails AA and the one message
 *  explaining the failure becomes the least readable thing on the panel. */
const channel = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const luminance = (hex) => {
  const h = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};
export const contrastRatio = (fg, bg) => {
  const [a, b] = [luminance(fg), luminance(bg)].sort((x, y) => y - x);
  return (a + 0.05) / (b + 0.05);
};

/** #5F6368 measured 3.47:1 on #000 — below the 4.5:1 AA floor for normal
 *  text. This is 8.6:1 and still reads as secondary. */
export const FALLBACK_FG = "#9AA0A6";

const PanelErrorBoundary = ({ panelName, children }) => (
  <Sentry.ErrorBoundary
    // A remount of the failing subtree is not attempted: the panels own their
    // own retry/poll, and resetting here would loop a deterministic bug.
    fallback={
      <div
        role="status"
        style={{
          padding: "16px",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          color: FALLBACK_FG,
          fontSize: "0.9rem",
        }}
      >
        <strong>{panelName}</strong> couldn&apos;t load. This is a problem with
        the page, not with the client&apos;s record — the information may still
        be there. Refreshing usually fixes it.
      </div>
    }
    beforeCapture={(scope) => {
      // The panel name is the one piece of context that makes the event
      // actionable, and it carries no client data.
      scope.setTag("panel", panelName);
      scope.setTag("boundary", "panel");
    }}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default PanelErrorBoundary;
